import React, { useEffect, useRef, useState } from 'react';

import { Editor, JSONContent } from '@tiptap/react';
import { Alert, Button, Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useTranslation } from 'react-i18next';

import { MainContentContainer, ContentContainer } from 'common-ui/containers';
import { conversationsApi, useAppSelector, selectWebSocketStatus, useAppDispatch, resetMessagesState } from 'core/lib';
import { HubConnectionState } from 'core/lib/utils/signalR/utils';
import { useHub } from 'utils/hooks/hubHooks';
import LoaderWrapper from 'utils/loader-wrapper';

import { ConversationsWysiwygEditor, DirectConversationDetailsHeader, MessagesListing } from '../components';

type DirectConversationDetailsProps = {
  conversationId: number;
};

const DirectConversationDetails = ({ conversationId }: DirectConversationDetailsProps) => {
  const { t } = useTranslation();
  const [editor, setEditor] = useState<Editor | null>(null);
  const [form] = useForm();
  const infiniteScrollRef = useRef<HTMLDivElement>(null);
  const [hub] = useHub();

  const webSocketStatus = useAppSelector(selectWebSocketStatus);

  const {
    data: conversationDetails,
    isLoading: loadingConversationDetails,
    isSuccess: conversationLoaded,
  } = conversationsApi.useGetDirectConversationQuery(conversationId, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  const dispatch = useAppDispatch();

  const [postMessage] = conversationsApi.usePostConversationMessageMutation();

  useEffect(() => {
    if (conversationLoaded && webSocketStatus === HubConnectionState.Connected) {
      hub.conversations.joinConversationRoom(conversationId);
    }
    return () => {
      hub.conversations.leaveConversationRoom();
    };
  }, [hub.conversations, webSocketStatus, conversationId, conversationLoaded]);

  useEffect(() => {
    if (conversationId) {
      dispatch(resetMessagesState(conversationId));
    }
  }, [conversationId]); //eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (draftMessage: { content: JSONContent }) => {
    if (conversationDetails && editor!.getText()) {
      postMessage({
        conversationId,
        content: JSON.stringify(draftMessage.content),
        text: editor!.getText(),
      })
        .unwrap()
        .then(() => {
          infiniteScrollRef.current?.scrollIntoView(false);
        });
      editor?.commands.clearContent();
      editor?.commands.clearNodes();
      editor?.commands.focus();
    }
  };

  return (
    <MainContentContainer id="conversation-details">
      <ContentContainer>
        <LoaderWrapper
          loading={loadingConversationDetails}
          message={t('loaders:myConversations.loadingConversationDetails')}
        >
          <div className="flex flex-col gap-2 h-full">
            {conversationDetails && (
              <DirectConversationDetailsHeader withDivider conversationDetails={conversationDetails} />
            )}
            <MessagesListing
              infiniteScrollRef={infiniteScrollRef}
              conversationId={conversationId}
              conversationTitle={conversationDetails?.peer.name}
              conversationParticipants={conversationDetails?.participants ?? []}
              totalMessages={conversationDetails?.totalMessages}
              loaderMessage={t('loaders:myConversations.loadingConversationMessages')}
            />
            {webSocketStatus === HubConnectionState.Disconnected && (
              <Alert
                message={t('global:placeholders.connectionLost')}
                showIcon
                type="error"
                action={
                  <Button
                    danger
                    type="primary"
                    disabled={
                      webSocketStatus === HubConnectionState.Connecting ||
                      webSocketStatus === HubConnectionState.Reconnecting
                    }
                    loading={
                      webSocketStatus === HubConnectionState.Connecting ||
                      webSocketStatus === HubConnectionState.Reconnecting
                    }
                    onClick={hub.conversations.disconnectAndRestart}
                  >
                    {t('actions:global.reconnect')}
                  </Button>
                }
              />
            )}
            {conversationDetails && (
              <Form onFinish={handleSubmit} initialValues={{ content: '' }} form={form} component={false}>
                <Form.Item name="content" className="m-0">
                  <ConversationsWysiwygEditor
                    onSubmit={form.submit}
                    setEditor={setEditor}
                    conversationParticipants={conversationDetails.participants}
                    placeholder={t('conversations:placeholders.messageInConversation', {
                      conversation: conversationDetails.peer.name,
                    })}
                  />
                </Form.Item>
              </Form>
            )}
          </div>
        </LoaderWrapper>
      </ContentContainer>
    </MainContentContainer>
  );
};

export default DirectConversationDetails;
